import { useState } from 'react';
import { BarChart3, Download, Users, School, BookOpen } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import '../../components/ui/datatable.css';
import { ROUTES } from '../../api/routes';

interface TurmaInfo {
  id: string;
  nome: string;
  serie: string;
  turno: string;
  totalAlunos: number;
}

interface ConsolidacaoResumo {
  totalAlunos: number;
  totalTurmas: number;
  mediaFrequencia: number;
  totalAusencias: number;
  totalIncidentes: number;
  totalAvaliacoes: number;
}

interface FreqAluno {
  alunoId: string;
  nome: string;
  matricula?: string;
  totalDias: number;
  presentes: number;
  ausentes: number;
  percentual: number;
  emRisco: boolean;
  porBimestre: { bimestre: number; totalDias: number; presentes: number; percentual: number }[];
}

export function RelatoriosPage() {
  const user = useAuthStore((s) => s.user);
  const escolaId = user?.escolaId || '';
  const [turmaId, setTurmaId] = useState('');
  const [tab, setTab] = useState<'consolidacao' | 'frequencia'>('consolidacao');

  // Consolidação da escola
  const { data: consolidacao, isLoading: loadingConsolidacao } = useQuery({
    queryKey: ['relatorio-consolidacao', escolaId],
    queryFn: async () => {
      const { data } = await api.get(ROUTES.relatorios.consolidacao(escolaId));
      return data as { resumo: ConsolidacaoResumo; turmas: TurmaInfo[] };
    },
    enabled: !!escolaId,
  });

  // Frequência por turma
  const { data: freqTurma, isLoading: loadingFreq } = useQuery({
    queryKey: ['relatorio-frequencia', escolaId, turmaId],
    queryFn: async () => {
      const { data } = await api.get(ROUTES.relatorios.frequencia(escolaId, turmaId));
      return data as { totalAlunos: number; mediaFrequencia: number; alunosEmRisco: number; alunos: FreqAluno[] };
    },
    enabled: !!escolaId && !!turmaId,
  });

  const turmas = consolidacao?.turmas || [];
  const resumo = consolidacao?.resumo;

  // Exportar CSV
  const exportarCSV = () => {
    if (!freqTurma?.alunos) return;
    const header = 'Nome,Matrícula,Total Dias,Presenças,Ausências,Frequência (%),Em Risco\n';
    const rows = freqTurma.alunos.map((a: FreqAluno) =>
      `"${a.nome}","${a.matricula || ''}",${a.totalDias},${a.presentes},${a.ausentes},${a.percentual},${a.emRisco ? 'Sim' : 'Não'}`
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `frequencia_turma_${turmaId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1>Relatórios</h1>
          <p>Consolidações e exportações</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {[
          { val: 'consolidacao' as const, label: 'Consolidação', icon: <School size={14} /> },
          { val: 'frequencia' as const, label: 'Frequência por Turma', icon: <BarChart3 size={14} /> },
        ].map(t => (
          <button
            key={t.val}
            onClick={() => setTab(t.val)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
              borderRadius: 8, fontSize: '0.82rem', fontWeight: 600,
              border: '1px solid var(--border-color)', cursor: 'pointer',
              background: tab === t.val ? '#3b82f6' : 'transparent',
              color: tab === t.val ? '#fff' : 'var(--text-muted)',
              borderColor: tab === t.val ? '#3b82f6' : undefined,
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ═══ TAB: Consolidação ═══ */}
      {tab === 'consolidacao' && (
        <>
          {loadingConsolidacao ? (
            <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>Carregando...</div>
          ) : resumo ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
                <div className="kpi-card hover-lift" style={{ gap: 6 }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)' }}>Alunos</span>
                  <span style={{ fontSize: '1.6rem', fontWeight: 800, color: '#3b82f6' }}>{resumo.totalAlunos}</span>
                </div>
                <div className="kpi-card hover-lift" style={{ gap: 6 }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)' }}>Turmas</span>
                  <span style={{ fontSize: '1.6rem', fontWeight: 800, color: '#7c3aed' }}>{resumo.totalTurmas}</span>
                </div>
                <div className="kpi-card hover-lift" style={{ gap: 6 }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)' }}>Freq. Média</span>
                  <span style={{ fontSize: '1.6rem', fontWeight: 800, color: resumo.mediaFrequencia >= 75 ? '#22c55e' : '#ef4444' }}>
                    {resumo.mediaFrequencia.toFixed(1)}%
                  </span>
                </div>
                <div className="kpi-card hover-lift" style={{ gap: 6 }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#ef4444' }}>Ausências</span>
                  <span style={{ fontSize: '1.6rem', fontWeight: 800, color: '#ef4444' }}>{resumo.totalAusencias}</span>
                </div>
                <div className="kpi-card hover-lift" style={{ gap: 6 }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#f59e0b' }}>Incidentes</span>
                  <span style={{ fontSize: '1.6rem', fontWeight: 800, color: '#f59e0b' }}>{resumo.totalIncidentes}</span>
                </div>
                <div className="kpi-card hover-lift" style={{ gap: 6 }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)' }}>Avaliações</span>
                  <span style={{ fontSize: '1.6rem', fontWeight: 800, color: '#7c3aed' }}>{resumo.totalAvaliacoes}</span>
                </div>
              </div>

              {/* Lista de turmas */}
              <div style={cardStyle}>
                <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Users size={16} style={{ color: '#3b82f6' }} /> Turmas
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
                  {turmas.map((t: TurmaInfo) => (
                    <div key={t.id} style={{
                      padding: '12px 14px', borderRadius: 10, background: 'var(--bg-main, #f9fafb)',
                      border: '1px solid var(--border-color)', cursor: 'pointer',
                    }}
                    onClick={() => { setTurmaId(t.id); setTab('frequencia'); }}
                    >
                      <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-main)' }}>{t.nome}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                        {t.serie} • {t.turno} • {t.totalAlunos} alunos
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>Nenhum dado disponível</div>
          )}
        </>
      )}

      {/* ═══ TAB: Frequência por Turma ═══ */}
      {tab === 'frequencia' && (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <select
              value={turmaId}
              onChange={e => setTurmaId(e.target.value)}
              style={{
                padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-color)',
                background: 'var(--bg-card)', color: 'var(--text-main)', fontSize: '0.82rem',
              }}
            >
              <option value="">Selecione a turma</option>
              {turmas.map((t: TurmaInfo) => <option key={t.id} value={t.id}>{t.nome} ({t.serie})</option>)}
            </select>
            {freqTurma && (
              <button onClick={exportarCSV} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
                borderRadius: 8, background: '#22c55e', color: '#fff', border: 'none',
                fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
              }}>
                <Download size={14} /> Exportar CSV
              </button>
            )}
          </div>

          {!turmaId ? (
            <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>Selecione uma turma acima</div>
          ) : loadingFreq ? (
            <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>Carregando...</div>
          ) : freqTurma ? (
            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: '0.92rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <BookOpen size={16} style={{ color: '#7c3aed' }} /> Frequência Detalhada
                </span>
                <div style={{ display: 'flex', gap: 12, fontSize: '0.72rem' }}>
                  <span>Alunos: <b>{freqTurma.totalAlunos}</b></span>
                  <span>Freq. média: <b style={{ color: freqTurma.mediaFrequencia >= 75 ? '#22c55e' : '#ef4444' }}>{freqTurma.mediaFrequencia.toFixed(1)}%</b></span>
                  <span style={{ color: '#ef4444' }}>Em risco: <b>{freqTurma.alunosEmRisco}</b></span>
                </div>
              </div>

              {/* Tabela */}
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table" style={{ width: '100%', fontSize: '0.78rem' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Aluno</th>
                      <th>B1</th><th>B2</th><th>B3</th><th>B4</th>
                      <th>Geral</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {freqTurma.alunos.map((a: FreqAluno) => (
                      <tr key={a.alunoId} style={{ background: a.emRisco ? 'rgba(239,68,68,0.04)' : undefined }}>
                        <td style={{ fontWeight: 600 }}>{a.nome}</td>
                        {[1,2,3,4].map(b => {
                          const bim = a.porBimestre.find(x => x.bimestre === b);
                          const pct = bim?.percentual ?? 0;
                          return (
                            <td key={b} style={{
                              textAlign: 'center', fontWeight: 600,
                              color: bim ? (pct >= 75 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#ef4444') : 'var(--text-muted)',
                            }}>
                              {bim ? `${pct.toFixed(0)}%` : '—'}
                            </td>
                          );
                        })}
                        <td style={{
                          textAlign: 'center', fontWeight: 800,
                          color: a.percentual >= 75 ? '#22c55e' : '#ef4444',
                        }}>
                          {a.percentual.toFixed(1)}%
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {a.emRisco ? (
                            <span style={{ padding: '2px 8px', borderRadius: 6, background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '0.65rem', fontWeight: 700 }}>
                              RISCO
                            </span>
                          ) : (
                            <span style={{ padding: '2px 8px', borderRadius: 6, background: 'rgba(34,197,94,0.1)', color: '#22c55e', fontSize: '0.65rem', fontWeight: 700 }}>
                              OK
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  borderRadius: 14, background: 'var(--bg-card, #fff)', padding: 16,
  border: '1px solid var(--border-color, #e5e7eb)',
};
